/** @typedef {import('@sentry/types').Integration} SentryIntegration */

import { getFramesFromEvent } from "@sentry/utils";
import { ACC_CONFIG } from "../../federated/accountCentre";

const applicationScopes = [
    {
        name: "account-centre",
        scopeId: ACC_CONFIG.sentryScopeId,
    },
    {
        name: "ocelot",
        scopeId: "scope-ocelot",
    },
];

const getFrameScopeId = (frame) => frame?.module_metadata?.scopeId;

const getCurrentEventScope = (frames) => {
    const lastErrorFrame = frames[frames.length - 1];
    const frameScopeId = getFrameScopeId(lastErrorFrame) || "";

    const matchedScope = applicationScopes.find(
        ({ scopeId }) => scopeId === frameScopeId
    );

    if (matchedScope) {
        return matchedScope.name;
    }

    return "unknown";
};

/**
 * sentryEventScopeTagsIntegration
 *
 * This integration allows you to tag events that contain frames originating from specific applications. Application files
 * are marked with a unique scopeId via the Sentry Webpack plugin at build time. These scopes are then used at runtime to
 * tag events that contain frames from those applications.
 *
 * @return {SentryIntegration}
 */
const sentryEventScopeTagsIntegration = () => {
    return {
        name: "SentryEventScopeTagsIntegration",
        processEvent(event) {
            const frames = getFramesFromEvent(event);

            if (frames && frames.length) {
                const eventScopeTags = {};
                // Check if each application scope is included in the event's error stack
                applicationScopes.forEach(({ name, scopeId }) => {
                    const isScopeIncludedInEventFrames = frames.some(
                        (frame) => getFrameScopeId(frame) === scopeId
                    );
                    eventScopeTags[`scope.${name}_inStackTrace`] =
                        isScopeIncludedInEventFrames;
                });

                event.tags = {
                    ...event.tags,
                    ...eventScopeTags,
                    "scope.error_scope": getCurrentEventScope(frames),
                };
            }

            return event;
        },
    };
};

export default sentryEventScopeTagsIntegration;
